<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, isArmed ? 'device-active--alarm' : '']"
      :style="{ '--pin-active-color': 'var(--device-color-alarm)' }"
      :title="device.deviceName">
      <!-- The drawn shield inside the status ring (P14) — the same fold the
           sheet's hero reads: armed (or triggered) fills the ring, disarmed
           keeps the track alone. -->
      <ControlStatusRing :value="isArmed || isTriggered ? 100 : 0">
        <ControlIcon
          :name="isArmed || isTriggered ? 'shield' : 'shield-off'"
          :size="pinIconSize"
          :style="isArmed || isTriggered ? { color: 'var(--device-color-alarm)' } : {}" />
      </ControlStatusRing>
      <div v-if="isArmed" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'security'"
      :name="displayName"
      :is-on="isArmed"
      active-color="red-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The chrome owns the head (card t_488df70b) and the sheet's alarm tint;
       this body is the shield hero + the four arm-mode chips the old body
       offered, off the same `setProperty('alarmState', …)` write. -->
  <template v-else-if="mode === 'control'">
    <div class="alarm-sheet">
      <div class="alarm-hero">
        <div class="alarm-circle" :class="alarmCircleClass">
          <ControlIcon :name="isArmed || isTriggered ? 'shield' : 'shield-off'" size="2.5rem" />
        </div>
        <div class="alarm-state" :class="alarmTextClass">{{ stateLabel }}</div>
      </div>

      <!-- The existing arm modes; the active one carries the live state word -->
      <div class="alarm-modes">
        <ControlChip
          v-for="armMode in ARM_MODES"
          :key="armMode.value"
          :disabled="controlling"
          :active="alarmState === armMode.value"
          :accent="armMode.accent"
          @click="handleSetState(armMode.value)">
          {{ tt(armMode.label, armMode.sl) }}
        </ControlChip>
      </div>

      <p v-if="lastUpdated" class="alarm-updated">{{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}</p>
    </div>
  </template>
</template>

<script setup lang="ts">
import type { Bilingual } from '../../config/bilingual';
import { formatTime } from '../../utils/formatDate';
import { computed, watch } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { tt } from '../../utils/consoleText';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

/**
 * The alarm's own state words. A `{ en, sl }` table read at render time, so the
 * reader's language is picked per paint (a module-scope `tt()` call would be
 * frozen at import).
 */
const STATE_LABELS: Record<string, Bilingual> = {
  disarmed: { en: 'Disarmed', sl: 'Deaktiviran' },
  armed_home: { en: 'Armed Home', sl: 'Aktiviran (doma)' },
  armed_away: { en: 'Armed Away', sl: 'Aktiviran (odsoten)' },
  armed_night: { en: 'Armed Night', sl: 'Aktiviran (noč)' },
  armed_vacation: { en: 'Armed Vacation', sl: 'Aktiviran (dopust)' },
  armed_custom_bypass: { en: 'Armed Custom', sl: 'Aktiviran (po meri)' },
  pending: { en: 'Pending', sl: 'V čakanju' },
  arming: { en: 'Arming', sl: 'Vklop v teku' },
  triggered: { en: 'Triggered!', sl: 'Sprožen!' },
};

/** One state's word, in the reader's language. */
function stateWord(key: string): string {
  const entry = STATE_LABELS[key];
  return entry ? tt(entry.en, entry.sl) : key;
}

/** The four arm modes this widget offers (the old body's four buttons). */
const ARM_MODES: Array<{ value: string; label: string; sl: string; accent: string }> = [
  { value: 'armed_away', label: 'Arm Away', sl: 'Vklopi (odsotnost)', accent: 'rgb(239, 68, 68)' },
  { value: 'armed_home', label: 'Arm Home', sl: 'Vklopi (doma)', accent: 'rgb(249, 115, 22)' },
  { value: 'armed_night', label: 'Arm Night', sl: 'Vklopi (noč)', accent: 'rgb(99, 102, 241)' },
  { value: 'disarmed', label: 'Disarm', sl: 'Izklopi', accent: 'var(--color-primary-500)' },
];

const alarmState = computed(() => {
  const val = currentValues.value['state'];
  if (typeof val === 'string') return val;
  const dv = props.device.deviceValues?.find((v) => v.valueKey === 'state');
  return (dv?.currentValue as string) ?? 'disarmed';
});

const isArmed = computed(() => alarmState.value.startsWith('armed'));
const isTriggered = computed(() => alarmState.value === 'triggered');
const stateLabel = computed(() => stateWord(alarmState.value));

/** The hero circle's tint — the old glyph's own semantics, as classes now. */
const alarmCircleClass = computed(() => {
  if (isTriggered.value) return 'alarm-circle--triggered';
  if (isArmed.value) return 'alarm-circle--armed';
  return 'alarm-circle--off';
});

const alarmTextClass = computed(() => {
  if (isTriggered.value) return 'text-red-600 dark:text-red-400';
  if (isArmed.value) return 'text-red-500 dark:text-red-400';
  return 'text-primary-600 dark:text-primary-400';
});

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

async function handleSetState(state: string) {
  await setProperty(props.device, 'alarmState', state);
}

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn shield's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});
</script>

<style scoped>
.active .device-icon span,
.active .device-name,
.active .device-current-value {
  color: var(--device-color-alarm) !important;
}
.active {
  border-color: var(--device-color-alarm) !important;
  background-color: transparent !important;
}
.alarm-triggered {
  border-color: rgb(239, 68, 68) !important;
  animation: alarm-flash 1s ease-in-out infinite;
}
@keyframes alarm-flash {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* ── Control mode: the sheet body ────────────────────────────────────── */
.alarm-sheet {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(1rem * var(--ui-space-factor));
}

.alarm-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: calc(0.25rem * var(--ui-space-factor));
}

/* The shield hero: red while armed, the alarm tone while triggered (the sheet
   flash itself is the chrome's — card t_488df70b — this only reads state). */
.alarm-circle {
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s;
}

.dark .alarm-circle {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-400));
}

.alarm-circle--armed {
  color: var(--device-color-alarm);
  border-color: color-mix(in srgb, var(--device-color-alarm) 50%, transparent);
  background: color-mix(in srgb, var(--device-color-alarm) 14%, transparent);
}

.alarm-circle--triggered {
  color: var(--device-color-alarm);
  border-color: color-mix(in srgb, var(--device-color-alarm) 65%, transparent);
  background: color-mix(in srgb, var(--device-color-alarm) 20%, transparent);
  box-shadow: 0 0 18px color-mix(in srgb, var(--device-color-alarm) 24%, transparent);
  animation: alarm-hero-breathe 1s ease-in-out infinite;
}

@keyframes alarm-hero-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

.alarm-state {
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.2;
}

.alarm-modes {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.alarm-updated {
  margin: 0;
  align-self: center;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .alarm-updated {
  color: rgb(var(--gray-400));
}

@media (prefers-reduced-motion: reduce) {
  .alarm-triggered {
    animation: none;
  }
  /* The tile's triggered glyph keeps its pulse guard — `animate-pulse` lives on
     the tile, so the old body's rule was never the only user. */
  .animate-pulse {
    animation: none;
  }
  .alarm-circle--triggered {
    animation: none;
  }
}
</style>
